import React from 'react';
import useToolStore from '../../../store/useToolStore';

const ApiGeneratorPreview = ({ config }) => {
  const apiResponse = useToolStore((state) => state.apiResponse);
  const apiError = useToolStore((state) => state.apiError);
  const isLoading = useToolStore((state) => state.isLoading);
  
  const buildUrl = () => {
    let url = '';
    if (config.url && config.url.startsWith('http')) {
      // Full URL provided
      url = config.url;
    } else if (config.baseURL) {
      // Build from baseURL + endpoint
      const endpoint = config.url || '';
      url = config.baseURL + (endpoint.startsWith('/') ? endpoint : '/' + endpoint);
    } else {
      url = config.url || config.baseURL || 'No URL configured';
    }
    
    const params = (config.queryParams || []).filter(p => p.key && p.value);
    if (params.length > 0) {
      const queryString = params.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
    return url;
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-900 p-4 lg:p-8 overflow-auto">
      <div className="max-w-4xl w-full bg-gray-800 rounded-lg p-6 space-y-6">
        {/* Request Section */}
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">API Request</h3>
          <div className="space-y-4">
            {/* Method and URL */}
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`px-3 py-1 rounded text-xs font-bold ${
                  config.method === 'GET' ? 'bg-green-600' :
                  config.method === 'POST' ? 'bg-blue-600' :
                  config.method === 'PUT' ? 'bg-yellow-600' :
                  config.method === 'DELETE' ? 'bg-red-600' :
                  'bg-gray-600'
                } text-white`}>
                  {config.method || 'GET'}
                </span>
                <code className="text-sm text-gray-300 font-mono break-all">{buildUrl()}</code>
              </div>
            </div>

            {/* Headers */}
            {(config.headers || []).filter(h => h.key && h.value).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Headers</h4>
                <div className="bg-gray-900 rounded p-3 space-y-1">
                  {(config.headers || []).filter(h => h.key && h.value).map((header, index) => (
                    <div key={index} className="text-xs font-mono text-gray-300">
                      <span className="text-gray-500">{header.key}:</span> {header.value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Query Params */}
            {(config.queryParams || []).filter(p => p.key && p.value).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Query Parameters</h4>
                <div className="bg-gray-900 rounded p-3 space-y-1">
                  {(config.queryParams || []).filter(p => p.key && p.value).map((param, index) => (
                    <div key={index} className="text-xs font-mono text-gray-300">
                      <span className="text-gray-500">{param.key}:</span> {param.value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Body */}
            {['POST', 'PUT', 'PATCH'].includes(config.method) && config.bodyContent && (
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Body ({config.bodyType})</h4>
                <div className="bg-gray-900 rounded p-3">
                  <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto">
                    {config.bodyContent}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Response Section */}
        {(apiResponse || apiError || isLoading) && (
          <div className="border-t border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">API Response</h3>
            
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <svg className="animate-spin h-8 w-8 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-400">Sending request...</span>
                </div>
              </div>
            )}
            
            {apiError && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-red-400 mb-2">Error</h4>
                <div className="text-sm text-red-300 font-mono">
                  <div className="mb-1"><span className="text-red-500">Name:</span> {apiError.name}</div>
                  <div><span className="text-red-500">Message:</span> {apiError.message}</div>
                  {apiError.stack && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-red-400">Stack Trace</summary>
                      <pre className="mt-2 text-xs overflow-x-auto">{apiError.stack}</pre>
                    </details>
                  )}
                </div>
              </div>
            )}
            
            {apiResponse && (
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Status</h4>
                  <div className="bg-gray-900 rounded p-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      apiResponse.status >= 200 && apiResponse.status < 300 ? 'bg-green-600' :
                      apiResponse.status >= 400 && apiResponse.status < 500 ? 'bg-yellow-600' :
                      apiResponse.status >= 500 ? 'bg-red-600' :
                      'bg-gray-600'
                    } text-white`}>
                      {apiResponse.status} {apiResponse.statusText}
                    </span>
                  </div>
                </div>

                {/* Response Headers */}
                {apiResponse.headers && Object.keys(apiResponse.headers).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Response Headers</h4>
                    <div className="bg-gray-900 rounded p-3 space-y-1 max-h-40 overflow-y-auto">
                      {Object.entries(apiResponse.headers).map(([key, value]) => (
                        <div key={key} className="text-xs font-mono text-gray-300">
                          <span className="text-gray-500">{key}:</span> {value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Response Body */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Response Body</h4>
                  <div className="bg-gray-900 rounded p-3">
                    <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto">
                      {typeof apiResponse.data === 'object' 
                        ? JSON.stringify(apiResponse.data, null, 2)
                        : apiResponse.raw || apiResponse.data
                      }
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {!apiResponse && !apiError && !isLoading && (
          <div className="border-t border-gray-700 pt-6">
            <div className="text-center text-gray-500 py-8">
              <p>Click "Test API" button to send request and see response here</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiGeneratorPreview;

